/**
 * CloseRoundButton — operator action to CLOSE a round (decision 12, issue #65).
 *
 * A completed round stays OPEN to reenvíos; closing it is an explicit action that
 * freezes the report and is the boundary of resubmissions. Lives on the round
 * dashboard (where the report lives) and is rendered ONLY for operators (the caller
 * gates by role, so we can safely fetch the operator-only status here).
 *
 * States:
 *  - already `closed` → a static "Ronda cerrada" chip (no action),
 *  - `running` → the button is disabled (wait for the batch to finish),
 *  - otherwise → "Cerrar ronda" opens a confirm dialog. A
 *    `ROUND_HAS_PENDING_RESUBMISSIONS` (409) is a WARNING, not a wall (PRD #107
 *    amendment 4, issue #127/#150 — close wins): the dialog shows the pending
 *    list + count and re-arms the confirm as an explicit force-close
 *    ("cerrar y abandonar N reenvíos") that re-POSTs with `force=true`; the
 *    abandoned expedientes stay visible as "No resueltos" in the report. Any
 *    other error is shown as a plain alert.
 */

import { useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { api, isApiError, useApiQuery } from "../api/useApi";
import { IS_DEMO } from "../api/client";
import type { BatchStatus } from "../api/client";

export function CloseRoundButton({
  roundId,
  onClosed,
}: {
  roundId: string;
  /** Called after a successful close, so the parent can refresh the report view. */
  onClosed?: () => void;
}) {
  // Operator-only status read (the caller only mounts this for operators): drives
  // whether the round is already closed / still running / closeable.
  const { data: status, reload: reloadStatus } = useApiQuery<BatchStatus | null>(
    async () => {
      if (IS_DEMO) return null;
      const res = await api.GET("/rounds/{round_id}/status", {
        params: { path: { round_id: roundId } },
      });
      return res.data ?? null;
    },
    [roundId],
  );

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Set from the pending-resubmissions 409: what a force close would abandon.
  // Non-null re-arms the confirm button as an explicit force-close.
  const [pendingClose, setPendingClose] = useState<{
    ids: string[];
    count: number;
  } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [closedLocally, setClosedLocally] = useState(false);

  const isClosed = closedLocally || status?.state === "closed";
  const isRunning = status?.state === "running";

  if (IS_DEMO) return null;

  if (isClosed) {
    return (
      <Chip
        icon={<LockIcon />}
        label="Ronda cerrada"
        variant="outlined"
        sx={{ fontWeight: 600 }}
      />
    );
  }

  const forceArmed = pendingClose !== null;

  async function handleConfirm() {
    setSubmitting(true);
    setFailure(null);
    try {
      await api.POST("/rounds/{round_id}/close", {
        params: {
          path: { round_id: roundId },
          // Second confirm after the 409: abandon the pending reenvíos.
          query: forceArmed ? { force: true } : undefined,
        },
      });
      setClosedLocally(true);
      setOpen(false);
      reloadStatus();
      onClosed?.();
    } catch (err) {
      if (isApiError(err) && err.code === "ROUND_HAS_PENDING_RESUBMISSIONS") {
        // Not a wall (close wins): surface the machine-readable pending list +
        // count and let the SAME dialog confirm the forced close explicitly.
        const ids = err.pendingExpedientes ?? [];
        setPendingClose({ ids, count: err.pendingCount ?? ids.length });
      } else if (isApiError(err)) {
        setFailure(err.detail ?? err.title ?? "No se pudo cerrar la ronda.");
      } else {
        setFailure("No se pudo cerrar la ronda.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Tooltip
        title={
          isRunning
            ? "Espera a que termine el procesamiento por lotes antes de cerrar."
            : ""
        }
      >
        <span>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<LockOutlinedIcon />}
            disabled={isRunning}
            onClick={() => {
              // Fresh dialog: disarm any force-close left from a prior attempt.
              setPendingClose(null);
              setFailure(null);
              setOpen(true);
            }}
          >
            Cerrar ronda
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => !submitting && setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cerrar la ronda {roundId}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cerrar la ronda congela el reporte final y termina la ventana de
            reenvíos: después no se podrán reenviar, re-validar ni adjudicar
            expedientes de esta ronda. Esta acción es permanente.
          </DialogContentText>

          {pendingClose && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {pendingClose.count} expediente(s) siguen en espera de reenvío.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Si cierras ahora, esos reenvíos pendientes se abandonan de forma
                permanente: quedarán como «No resueltos» en el reporte de la
                ronda. También puedes cancelar y resolverlos primero con un
                reenvío que llegue a veredicto o con una adjudicación definitiva
                (APPROVED/REJECTED).
              </Typography>
              <List dense disablePadding>
                {pendingClose.ids.map((id) => (
                  <ListItem key={id} disableGutters sx={{ py: 0 }}>
                    <ListItemText
                      primary={id}
                      slotProps={{
                        primary: { sx: { fontFamily: "monospace" } },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {failure && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {failure}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={forceArmed ? "error" : "warning"}
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting
              ? "Cerrando…"
              : pendingClose
                ? `Cerrar y abandonar ${pendingClose.count} reenvío(s)`
                : "Cerrar ronda"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
